import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useDebounce } from "use-debounce";

import { PermissionKey, PermissionScope, type PermissionScopeEnum } from "@fitness-app/data-models";
import { TeamMemberRole, TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { usePermissionScope } from "~/hooks/usePermission";
import { supabase, useAppSelector } from "~/store/initializeStore";

// TRE-2313 — wzmianki w kompozerze asystenta. @klientka: każdy, w zakresie, który daje
// mu RLS na `trainee` (trener swoje, head coach zespołu, owner i administracja całe
// konto). @trener: tylko z zakresem TEAM albo ALL, z rosteru, który Dashboard i tak
// pobiera (head coach dostaje w nim wyłącznie przypisanych trenerów). Zakres to najwęższy
// z `clients.read`, `chat.read` i `agents.use`, tak jak liczy go serwer - inaczej lista
// podsuwałaby osoby, o które asystent i tak odmówi. Serwer niczego stąd nie przyjmuje
// na wiarę - narzędzia sprawdzają zakres dla każdego id.

export type TrainerAssistantMention = {
  type: "client" | "trainer";
  id: string;
  name: string;
  // Tylko do wyświetlenia chipa w wysłanej wiadomości; serwer go ignoruje.
  avatarUrl?: string | null;
};

export type MentionOption = TrainerAssistantMention & { description: string };

const MENTIONABLE_TRAINER_ROLES = new Set<string>([TeamMemberRole.REGULAR_TRAINER, TeamMemberRole.SHARED_TRAINER]);
const CLIENT_LIMIT = 8;

const SCOPE_RANK: Record<PermissionScopeEnum, number> = {
  [PermissionScope.NONE]: 0,
  [PermissionScope.ASSIGNED]: 1,
  [PermissionScope.TEAM]: 2,
  [PermissionScope.ALL]: 3,
};

export const narrowestScope = (...scopes: PermissionScopeEnum[]): PermissionScopeEnum =>
  scopes.reduce((narrowest, scope) => (SCOPE_RANK[scope] < SCOPE_RANK[narrowest] ? scope : narrowest));

const useAssistantScope = () =>
  narrowestScope(
    usePermissionScope(PermissionKey.CLIENTS_READ),
    usePermissionScope(PermissionKey.CHAT_READ),
    usePermissionScope(PermissionKey.AGENTS_USE),
  );

// Znaki, które łamią składnię filtra `or` PostgREST-a albo działają jak wildcard.
const sanitizeSearch = (value: string) => value.replace(/[,()*%\\:]/g, " ").trim();

const fullName = (firstName: string | null, lastName: string | null) =>
  [firstName, lastName].filter(Boolean).join(" ").trim();

export const useMentionOptions = (search: string, enabled: boolean): { options: MentionOption[]; loading: boolean } => {
  const [debouncedSearch] = useDebounce(search, 250);
  const term = sanitizeSearch(debouncedSearch);
  const scope = useAssistantScope();
  const canMentionTrainers = scope === PermissionScope.TEAM || scope === PermissionScope.ALL;
  const trainers = useAppSelector((state) => state.team.trainers);
  const memberId = useAppSelector((state) => state.team.selectedMember?.id ?? null);
  const assignedTrainers = useAppSelector((state) => state.team.assignedTrainers);

  // null = całe konto (zostaje RLS); inaczej tylko klientki tych opiekunów.
  const assignees = useMemo(() => {
    if (scope === PermissionScope.ALL) return null;
    if (!memberId) return [];
    if (scope === PermissionScope.TEAM) return [memberId, ...assignedTrainers.map((item) => item.trainer.id)];
    return [memberId];
  }, [assignedTrainers, memberId, scope]);

  const { data: clients, isFetching } = useQuery({
    queryKey: ["trainerAssistantMentionClients", term, assignees],
    enabled: enabled && scope !== PermissionScope.NONE,
    staleTime: 30_000,
    queryFn: async () => {
      let query = supabase
        .from("trainee")
        .select('id, "firstName", "lastName", email, status, user:userId(avatarUrl)')
        .order("status", { ascending: true })
        .limit(CLIENT_LIMIT);
      if (assignees) query = query.in("assignedTrainerId", assignees);

      const words = term.split(/\s+/).filter(Boolean);
      if (words.length > 1) {
        // Imię i nazwisko w dowolnej kolejności.
        const [first, last] = [words[0], words.at(-1)];
        query = query.or(
          `and(firstName.ilike.%${first}%,lastName.ilike.%${last}%),and(firstName.ilike.%${last}%,lastName.ilike.%${first}%)`,
        );
      } else if (term) {
        query = query.or(`firstName.ilike.%${term}%,lastName.ilike.%${term}%,email.ilike.%${term}%`);
      }

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  const options = useMemo(() => {
    const needle = term.toLowerCase();
    const trainerOptions: MentionOption[] = canMentionTrainers
      ? trainers
          .filter(
            (trainer) =>
              trainer.id !== memberId &&
              // TEAM: tylko przypisani trenerzy - roster bywa szerszy (team.read na całe konto).
              (!assignees || assignees.includes(trainer.id)) &&
              trainer.status === TeamMemberStatus.ACTIVE &&
              MENTIONABLE_TRAINER_ROLES.has(trainer.role),
          )
          .map((trainer) => ({
            type: "trainer" as const,
            id: trainer.id,
            name: fullName(trainer.firstName, trainer.lastName) || trainer.email,
            description: trainer.role === TeamMemberRole.SHARED_TRAINER ? "Head coach" : "Trener",
            avatarUrl: trainer.user?.avatarUrl || null,
          }))
          .filter((option) => !needle || option.name.toLowerCase().includes(needle))
          .slice(0, 5)
      : [];

    const clientOptions: MentionOption[] = (clients ?? []).map((client) => ({
      type: "client" as const,
      id: client.id,
      name: fullName(client.firstName, client.lastName) || client.email,
      description: client.status === "ACTIVE" ? client.email : `${client.email} · nieaktywna`,
      avatarUrl: (client.user as { avatarUrl: string | null } | null)?.avatarUrl ?? null,
    }));

    return [...trainerOptions, ...clientOptions];
  }, [assignees, canMentionTrainers, clients, memberId, term, trainers]);

  return { options, loading: isFetching };
};

/**
 * Czy tekst po `@` to nadal wyszukiwanie: najwyżej dwa słowa (imię i nazwisko), bez nowej linii.
 * Tekst za już wybraną wzmianką („@Anna Kowalska jak idzie”) nie otwiera listy ponownie.
 */
export const isMentionSearch = (search: string, picked: Map<string, TrainerAssistantMention>) => {
  if (search.includes("\n") || search.startsWith(" ")) return false;
  // Cała nazwa wybranej osoby (i ewentualnie dalszy tekst po spacji) - „@Annabelle” po wybraniu „@Anna” dalej szuka.
  if ([...picked.values()].some((mention) => search === mention.name || search.startsWith(`${mention.name} `)))
    return false;
  return search.trim().split(/\s+/).length <= 2 && !/\s\S+\s/.test(search);
};

/** Wzmianki, których `@Imię Nazwisko` nadal stoi w tekście (wybrane, a potem skasowane odpadają). */
export const mentionsInText = (text: string, picked: Map<string, TrainerAssistantMention>) =>
  [...picked.values()].filter((mention) => text.includes(`@${mention.name}`));

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Tnie tekst na kawałki: zwykły tekst i tokeny `@Imię Nazwisko` znanych wzmianek.
 * Dłuższe nazwy idą pierwsze, żeby „@Anna Kowalska” nie złapało się jako „@Anna”.
 */
export const splitByMentions = (
  text: string,
  mentions: TrainerAssistantMention[],
): (string | TrainerAssistantMention)[] => {
  if (!mentions.length) return [text];
  const byToken = new Map(mentions.map((mention) => [`@${mention.name}`, mention]));
  const tokens = [...byToken.keys()].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${tokens.map(escapeRegExp).join("|")})`, "g");
  return text
    .split(pattern)
    .filter(Boolean)
    .map((part) => byToken.get(part) ?? part);
};

export const readMessageMentions = (metadata: unknown): TrainerAssistantMention[] => {
  const mentions = (metadata as { mentions?: unknown } | null | undefined)?.mentions;
  return Array.isArray(mentions)
    ? mentions.filter(
        (mention): mention is TrainerAssistantMention =>
          Boolean(mention) && typeof mention.name === "string" && typeof mention.id === "string",
      )
    : [];
};
